import { ForwardedRef, forwardRef, useMemo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { isFunction, isObject } from '@xemplo/common-util';
import { useMergeRefs } from '@xemplo/ref-utils';

import { invalidTime } from './helpers/time-picker-rhf.helper';
import { TimePicker } from './time-picker';
import { TimePickerRhfProps } from './time-picker.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

export const TimePickerRhf = forwardRef(
  (props: TimePickerRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { rules, ...rest } = props;
    const { control } = useFormContext();

    const renderField = useMemo(() => fieldFactory({ ...props }, ref), [props, ref]);
    return (
      <Controller
        name={rest.name}
        rules={{
          ...rules,
          validate: (value, formValues) => {
            // This embeds the invalidTime validation
            // along with custom validation rules received by the props.
            if (value && invalidTime(value, props.timeInputType))
              return 'Invalid time format';
            if (isFunction(rules?.validate)) return rules.validate(value, formValues);
            if (isObject(rules?.validate)) {
              for (const key in rules.validate) {
                const result = rules.validate[key](value, formValues);
                if (result !== true) return result;
              }
            }
            return true;
          },
        }}
        control={control}
        render={renderField}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 **/
const fieldFactory = (
  props: TimePickerRhfProps,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  return ({ field, fieldState }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      field.onChange(newValue);
      props.onChange?.(e, newValue);
    };

    return (
      <TimePicker
        {...props}
        required={!!props.rules?.required}
        ref={mergedRefs}
        onInputChange={handleInputChange}
        onListChange={field.onChange}
        onBlur={(e) => {
          field.onBlur();
          props.onBlur?.(e);
        }}
        value={field.value ?? ''}
        errorMessage={fieldState.error?.message?.toString()}
      />
    );
  };
};
